import React, { Suspense, Component, lazy } from 'react';
import { Set, Map, OrderedSet } from 'immutable';
import CompanyWorkspaceContext from '@/pages/company/WorkspaceRoute/CompanyWorkspaceContext';
import ScrollManager from '@/lib/scrollManager';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import Box from '@mui/material/Box';
import TileGrid from '@/pages/company/WorkspaceRoute/components/TileGrid';
import CompanyBulkPanel from '@/pages/company/WorkspaceRoute/components/CompanyBulkPanel';
import LinkedDocumentsPanel from '@/pages/company/WorkspaceRoute/components/LinkedDocumentsPanel/LinkedPanel';
import StubWhenEmpty from '@/pages/company/WorkspaceRoute/components/stub/StubWhenEmpty';
import elements from '@/components/elements';

const Grid = lazy(() => import('@/pages/company/WorkspaceRoute/components/Grid'));

class WorkSpace extends Component {
  documentItem;

  constructor(props) {
    super(props);
    this.state = {
      isLinkedPanelSelected: false,
      linked: null,
    };
  }

  onPreview = (preview) => {
    const { onPreview } = this.props;
    onPreview(preview);
  };

  get selectedDoc() {
    const { selected } = this.props;
    const { linked } = this.state;
    return linked ? new Set() : selected;
  }

  get isGrid() {
    const { workSpaceType } = this.props;

    return workSpaceType === 'grid';
  }

  handleClearSelection = () => {
    const { onChangeSelected } = this.props;
    this.setState({
      linked: undefined,
    });
    onChangeSelected(new Set());
  };

  handleOutsideClick = ({ target }) => {
    // dont close bulk panel if its Confirm Dialog or another Dialog
    const targetIsDialog = target.closest('[role="presentation"]');

    if (!targetIsDialog) {
      this.handleClearSelection();
    }
  };

  filterSelectedDocs = (docs, isLinkedPanelSelected) =>
    docs.filter((doc) => {
      const linkid = doc.get('linkid');
      return !((linkid && linkid.endsWith('locked')) || (!isLinkedPanelSelected && linkid));
    });

  toggleSelection = (docs, isLinkedPanelSelected, isSelectAll) => {
    const { selected, documents, onChangeSelected } = this.props;
    const { isLinkedPanelSelected: isLinkedPanelSelectedState } = this.state;
    const docsMap = docs.size ? docs : new Map({ [docs.documentID]: docs });
    const filteredDocs = this.filterSelectedDocs(docsMap, isLinkedPanelSelected);
    const isSameItems = !!isLinkedPanelSelectedState === !!isLinkedPanelSelected;
    const getAllDocumentsList = () => {
      if (this.isGrid && !isLinkedPanelSelected) {
        return documents.keySeq().toArray();
      }
      return filteredDocs.keys();
    };
    const uSelected =
      !isSameItems || isSelectAll
        ? new Set(getAllDocumentsList())
        : filteredDocs.reduce((res, doc) => {
            const docId = doc.documentID;
            return res.has(docId) ? res.delete(docId) : res.add(docId);
          }, selected);

    if (!isSameItems) {
      this.setState({ isLinkedPanelSelected });
    }

    onChangeSelected(uSelected);
  };

  resetIsLinkedPanelSelected = () => {
    this.setState({ isLinkedPanelSelected: false });
  };

  actionWrapper = (cb) => (e, doc, isLinkedPanelItem) => {
    if (e.ctrlKey || e.metaKey) {
      e.stopPropagation();
      e.preventDefault();
      this.toggleSelection(doc, isLinkedPanelItem);
    } else {
      cb(doc.documentID, isLinkedPanelItem);
    }
  };

  handleClick = (...args) => {
    const { setNavigation } = this.props;
    return this.actionWrapper(setNavigation)(...args);
  };

  handleOpenNotes = (...args) => {
    const { openNotes } = this.props;
    return this.actionWrapper(openNotes)(...args);
  };

  handleOpenDocumentView = (documentID, isLinked) => {
    const { setNavigation } = this.props;
    setNavigation(documentID, isLinked);
  };

  handleSelectAll = () => {
    const { documents, linkedDocs } = this.props;
    const [docs, isLinkedPanelItem] = linkedDocs.size ? [linkedDocs, true] : [documents, false];
    this.toggleSelection(docs, isLinkedPanelItem, true);
  };

  render() {
    const {
      documents,
      match,
      moveDocumentToCategory,
      getDocumentLinkedCount,
      onContextMenu,
      onChangeSelected,
      selected,
      gridRef,
      onShowLinked,
      preview,
      onMoveToCompany,
      onOpenSendByEmailDialog,
      moveToBundleOptions,
      workSpaceType,
      isTrashCategory,
    } = this.props;
    const { isLinkedPanelSelected, linked } = this.state;

    return (
      <ClickAwayListener onClickAway={this.handleOutsideClick}>
        {/* ClickAwayListener requires one child element, height 100% - for ag-grid */}
        <Box display="flex" height="100%" data-element={elements.content.workspace.documentsList}>
          <Box flexGrow={1}>
            {this.isGrid ? (
              <Suspense fallback={<div />}>
                <Grid
                  onPreview={this.onPreview}
                  onContextMenu={onContextMenu}
                  onChangeSelected={onChangeSelected}
                  onDocumentOpen={this.handleOpenDocumentView}
                  selected={selected}
                  forwardedRef={gridRef}
                  onShowLinked={onShowLinked}
                  resetIsLinkedPanelSelected={this.resetIsLinkedPanelSelected}
                  isLinkedPanelSelected={isLinkedPanelSelected}
                  preview={preview}
                  isTrashCategory={isTrashCategory}
                />
              </Suspense>
            ) : (
              <>
                <ScrollManager isTargetLoaded />

                <TileGrid
                  onClick={this.handleClick}
                  onPreview={this.onPreview}
                  preview={preview}
                  onShowLinked={onShowLinked}
                  onContextMenu={onContextMenu}
                  openNotes={this.handleOpenNotes}
                  isLinkedPanelSelected={isLinkedPanelSelected}
                  selectedDocuments={this.selectedDoc}
                  clearSelection={this.handleClearSelection}
                  getDocumentLinkedCount={getDocumentLinkedCount}
                  isTrashCategory={isTrashCategory}
                />
                {documents.size === 0 && <StubWhenEmpty />}
              </>
            )}
          </Box>

          <LinkedDocumentsPanel
            disabled={preview.documentId}
            onContextMenu={onContextMenu}
            onClick={this.handleClick}
            onPreview={this.onPreview}
            preview={preview}
            openNotes={this.handleOpenNotes}
            selectedDocuments={!isLinkedPanelSelected && this.isGrid ? new OrderedSet() : selected}
            clearSelection={this.handleClearSelection}
            onMoveToCompany={onMoveToCompany}
            onOpenSendByEmailDialog={onOpenSendByEmailDialog}
            moveToBundleOptions={moveToBundleOptions}
            getDocumentLinkedCount={getDocumentLinkedCount}
          />

          <CompanyBulkPanel
            disabled={preview.documentId}
            documentsIds={selected}
            isLinkedPanelSelected={isLinkedPanelSelected}
            linkid={linked}
            selectAll={this.handleSelectAll}
            clearSelection={this.handleClearSelection}
            companyId={match.params.companyId}
            currentRootCategory={match.params.category1}
            moveDocumentToCategory={moveDocumentToCategory}
            gridRef={gridRef}
            workSpaceType={workSpaceType}
          />
        </Box>
      </ClickAwayListener>
    );
  }
}

WorkSpace.contextType = CompanyWorkspaceContext;

export default WorkSpace;
